import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { useTranslation } from "react-i18next";

import PageTitle from "../../components/PageTitle";
import apiClient from "../../utils/apiClient";
import { brandColor } from "../../theme";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import Provider from "../../enum/Provider.enum";
import StyledDrawer from "../../components/StyledDrawer";
import { ActPrintType, ActViewer } from "../../components/ActViewer";
import ActAction from "../../enum/ActAction.enum";
import ArmeniaActsCell from "../../components/features/armenia/acts/ArmeniaActsCell";
import ArmeniaActsHistoryDrawer, {
  ArmeniaAct,
} from "../../components/features/armenia/acts/ArmeniaActsHistoryDrawer";
import ArmeniaActDisputeDrawer from "../../components/features/armenia/acts/ArmeniaActDisputeDrawer";
import ArmeniaActSignedFilesDrawer, {
  ArmeniaSignedActFile,
} from "../../components/features/armenia/acts/ArmeniaActSignedFilesDrawer";
import { PdfViewer } from "../../components/PdfViewer";
import { ImageViewer } from "../../components/ImageViewer";
import {
  getArmeniaActMetadata,
  getArmeniaActsByAgent,
} from "./armeniaActs.utils";

type ArmeniaDetailingSummary = {
  brokerage: number;
  retention: number;
  acquisition: number;
  total: number;
};

type ArmeniaDetailingRow = {
  id: string;
  fullname?: string;
  agent_tn_id?: string;
  agent_pager?: string;
  client_account?: string;
  date?: string;
  brokerage: number;
  retention: number;
  acquisition: number;
  total?: number;
  details?: ArmeniaDetailingRow[];
};

const defaultSummary: ArmeniaDetailingSummary = {
  brokerage: 0,
  retention: 0,
  acquisition: 0,
  total: 0,
};

const gridSx = {
  "--DataGrid-rowBorderColor": "#D1D3D4",
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    fontSize: 12,
    lineHeight: "110%",
  },
  "& .MuiDataGrid-cell": {
    fontSize: 16,
    borderTop: "0.5px solid #D1D3D4",
  },
  fontFamily: "inherit",
  borderWidth: 0,
  background: "#ffffff",
};

const DetailingAm = () => {
  const { t } = useTranslation();
  const { isAdmin } = useSelector((state: RootState) => state.user);
  const provider = useSelector((state: RootState) => state.app.provider);
  const usesUsdAndEnglishAct = [Provider.UAE, Provider.TFOS].includes(provider);

  const [month, setMonth] = useState<string>(dayjs().format("MM"));
  const [year, setYear] = useState<string>(dayjs().format("YYYY"));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<ArmeniaDetailingRow[]>([]);
  const [summary, setSummary] =
    useState<ArmeniaDetailingSummary>(defaultSummary);
  const [currency, setCurrency] = useState<string>(
    usesUsdAndEnglishAct ? "USD" : "AMD",
  );
  const [acts, setActs] = useState<ArmeniaAct[]>([]);

  const [showDetailsPopup, setShowDetailsPopup] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] =
    useState<ArmeniaDetailingRow | null>(null);

  const [showActsPopup, setShowActsPopup] = useState<boolean>(false);
  const [selectedActsAgent, setSelectedActsAgent] =
    useState<ArmeniaDetailingRow | null>(null);

  const [showDisputePopup, setShowDisputePopup] = useState<boolean>(false);
  const [showDisputePreviewPopup, setShowDisputePreviewPopup] =
    useState<boolean>(false);
  const [selectedDisputeAct, setSelectedDisputeAct] =
    useState<ArmeniaAct | null>(null);

  const [selectedAct, setSelectedAct] = useState<ArmeniaAct | undefined>();
  const [actType, setActType] = useState<ActPrintType>(ActPrintType.ACT);

  const [actLang, setActLang] = useState<"ru" | "en">(
    usesUsdAndEnglishAct ? "en" : "ru",
  );

  const [showSignedFilesPopup, setShowSignedFilesPopup] = useState(false);
  const [selectedSignedFilesAct, setSelectedSignedFilesAct] =
    useState<ArmeniaAct | null>(null);

  const years = Array.from(Array(5).keys()).map(
    (i) => +dayjs().format("YYYY") - i,
  );

  const [previewPdfBlob, setPreviewPdfBlob] = useState<Blob | undefined>();
  const [previewImageBlob, setPreviewImageBlob] = useState<Blob | undefined>();
  const [previewFileName, setPreviewFileName] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string>();

  const [isDisputeSubmitting, setIsDisputeSubmitting] = useState(false);

  const uploadSignedAct = async (act: ArmeniaAct) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.jpg,.jpeg,.png";

    input.onchange = async () => {
      const file = input.files?.[0];

      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        await apiClient.post(`/acts/${act.id}/signed-files`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        await loadReport();
      } catch (error) {
        console.error("Ошибка загрузки подписанного акта:", error);
        alert(t("detailingAm.errors.signedActUpload"));
      }
    };

    input.click();
  };

  const submitDispute = async ({
    accounts,
    comment,
  }: {
    accounts: string[];
    comment: string;
  }) => {
    if (!selectedDisputeAct?.id) return;

    try {
      setIsDisputeSubmitting(true);

      const response = await apiClient.post(
        `/acts/${selectedDisputeAct.id}/dispute`,
        {
          accounts,
          comment,
        },
      );

      if (response.data.success) {
        setShowDisputePopup(false);
        setSelectedDisputeAct(null);
        await loadReport();
      }
    } catch (error) {
      console.error("Ошибка оспаривания акта:", error);
    } finally {
      setIsDisputeSubmitting(false);
    }
  };

  const formatMoney = (value: unknown) => {
    const number = Number(value ?? 0);

    return number.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getActsForRow = (row?: ArmeniaDetailingRow | null) =>
    getArmeniaActsByAgent({
      acts,
      pager: row?.agent_pager,
      agentTnId: row?.agent_tn_id,
      isAdmin,
    });

  const getSelectedAgentActs = () => {
    if (isAdmin) {
      return getActsForRow(selectedActsAgent);
    }

    return [...acts].sort(
      (a, b) => Number(b.version ?? 0) - Number(a.version ?? 0),
    );
  };

  const getActClients = (act?: ArmeniaAct | null) => {
    const metadata = getArmeniaActMetadata(act);
    return Array.isArray(metadata?.clients) ? metadata.clients : [];
  };

  const previewSignedFile = async (file: ArmeniaSignedActFile) => {
    try {
      setPreviewLoading(true);
      setPreviewError(undefined);
      setPreviewPdfBlob(undefined);
      setPreviewImageBlob(undefined);
      setPreviewFileName(file.original_name || `file_${file.id}`);

      const response = await apiClient.get(`/acts/signed-files/${file.id}`, {
        responseType: "arraybuffer",
      });

      const contentType =
        file.mime_type ||
        response.headers["content-type"] ||
        "application/octet-stream";

      const blob = new Blob([response.data], { type: contentType });

      if (contentType === "application/pdf") {
        setPreviewPdfBlob(blob);
        return;
      }

      if (contentType.startsWith("image/")) {
        setPreviewImageBlob(blob);
        return;
      }

      setPreviewError(t("detailingAm.errors.previewUnsupported"));
    } catch (error) {
      console.error("Ошибка предпросмотра файла:", error);
      setPreviewError(t("detailingAm.errors.preview"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const agreeAct = async (act: ArmeniaAct) => {
    try {
      const response = await apiClient.get(`/acts/${act.id}/agree`);

      if (response.data.success) {
        await loadReport();
      }
    } catch (error) {
      console.error("Ошибка согласования акта:", error);
    }
  };

  const mainColumns = useMemo<GridColDef<ArmeniaDetailingRow>[]>(
    () => [
      {
        field: "fullname",
        headerName: isAdmin
          ? t("detailingAm.columns.agent")
          : t("detailingAm.columns.clientAccount"),
        flex: 2,
        valueGetter: (_, row) => row.fullname ?? row.client_account ?? row.id,
      },
      {
        field: "acts",
        headerName: t("detailingAm.columns.acts"),
        width: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ArmeniaActsCell
            acts={getActsForRow(params.row)}
            onOpen={() => {
              setSelectedActsAgent(params.row);
              setShowActsPopup(true);
            }}
          />
        ),
      },
      {
        field: "brokerage",
        headerName: t("detailingAm.columns.brokerageWithCurrency", {
          currency,
        }),
        flex: 1,
        type: "number",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "retention",
        headerName: t("detailingAm.columns.retentionWithCurrency", {
          currency,
        }),
        flex: 1,
        type: "number",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "acquisition",
        headerName: t("detailingAm.columns.acquisitionWithCurrency", {
          currency,
        }),
        flex: 1,
        type: "number",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "total",
        headerName: t("detailingAm.columns.totalWithCurrency", { currency }),
        flex: 1,
        type: "number",
        valueGetter: (_, row) =>
          Number(row.total ?? 0) ||
          Number(row.brokerage ?? 0) +
            Number(row.retention ?? 0) +
            Number(row.acquisition ?? 0),
        valueFormatter: (value) => formatMoney(value),
      },
    ],
    [isAdmin, acts, currency, provider, t],
  );

  const detailColumns = useMemo<GridColDef<ArmeniaDetailingRow>[]>(
    () => [
      {
        field: "client_account",
        headerName: t("detailingAm.columns.clientAccount"),
        flex: 2,
      },
      {
        field: "brokerage",
        headerName: t("detailingAm.columns.brokerageBaseWithCurrency", {
          currency,
        }),
        flex: 1,
        type: "number",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "retention",
        headerName: t("detailingAm.columns.retentionBaseWithCurrency", {
          currency,
        }),
        flex: 1,
        type: "number",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "acquisition",
        headerName: t("detailingAm.columns.acquisitionBaseWithCurrency", {
          currency,
        }),
        flex: 1,
        type: "number",
        valueFormatter: (value) => formatMoney(value),
      },
    ],
    [currency, t],
  );

  const handleCloseDetailsPopup = () => {
    setShowDetailsPopup(false);
    setSelectedAgent(null);
  };

  const loadReport = async () => {
    try {
      setIsLoading(true);

      const response = await apiClient.get(
        `/detailing-armenia?month=${month}&year=${year}`,
      );

      const actsResponse = await apiClient.get(
        `/acts?month=${month}&year=${year}`,
      );

      if (response.data.success) {
        setRows(response.data.rows ?? []);
        setSummary(response.data.summary ?? defaultSummary);
        setCurrency(
          response.data.currency ?? (usesUsdAndEnglishAct ? "USD" : "AMD"),
        );

        if (actsResponse.data.success) {
          setActs(actsResponse.data.rows ?? []);
        }

        handleCloseDetailsPopup();
      } else {
        console.error("Ошибка получения детализации Армении");
      }
    } catch (error) {
      console.error("Ошибка запроса детализации Армении:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const DetailsContent = () => (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{
          p: 1,
          borderLeft: "3px solid " + brandColor,
          backgroundColor: "#FFFDE7",
          mb: 2,
        }}
      >
        <Typography variant="body2">
          {t("detailingAm.summary.brokerage")}:{" "}
          <strong>{formatMoney(selectedAgent?.brokerage)}</strong>
        </Typography>

        <Typography variant="body2">
          {t("detailingAm.summary.retention")}:{" "}
          <strong>{formatMoney(selectedAgent?.retention)}</strong>
        </Typography>

        <Typography variant="body2">
          {t("detailingAm.summary.acquisition")}:{" "}
          <strong>{formatMoney(selectedAgent?.acquisition)}</strong>
        </Typography>

        <Typography variant="body2">
          {t("detailingAm.summary.total")}:{" "}
          <strong>{formatMoney(selectedAgent?.total)}</strong>
        </Typography>
      </Stack>

      <DataGrid
        rows={selectedAgent?.details ?? []}
        columns={detailColumns}
        getRowId={(row) => row.id}
        pagination
        sx={gridSx}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
      />
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>{t("detailingAm.metaTitle")}</title>
        <meta name="description" content={t("detailingAm.metaDescription")} />
      </Helmet>

      <PageTitle>{t("detailingAm.title")}</PageTitle>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel id="armenia-detailing-month-label">
            {t("detailingAm.month")}
          </InputLabel>
          <Select
            labelId="armenia-detailing-month-label"
            value={month}
            label={t("detailingAm.month")}
            size="small"
            onChange={(e) => setMonth(e.target.value)}
          >
            <MenuItem value="01">{t("detailingAm.months.january")}</MenuItem>
            <MenuItem value="02">{t("detailingAm.months.february")}</MenuItem>
            <MenuItem value="03">{t("detailingAm.months.march")}</MenuItem>
            <MenuItem value="04">{t("detailingAm.months.april")}</MenuItem>
            <MenuItem value="05">{t("detailingAm.months.may")}</MenuItem>
            <MenuItem value="06">{t("detailingAm.months.june")}</MenuItem>
            <MenuItem value="07">{t("detailingAm.months.july")}</MenuItem>
            <MenuItem value="08">{t("detailingAm.months.august")}</MenuItem>
            <MenuItem value="09">{t("detailingAm.months.september")}</MenuItem>
            <MenuItem value="10">{t("detailingAm.months.october")}</MenuItem>
            <MenuItem value="11">{t("detailingAm.months.november")}</MenuItem>
            <MenuItem value="12">{t("detailingAm.months.december")}</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel id="armenia-detailing-year-label">
            {t("detailingAm.year")}
          </InputLabel>
          <Select
            labelId="armenia-detailing-year-label"
            value={year}
            label={t("detailingAm.year")}
            size="small"
            onChange={(e) => setYear(e.target.value)}
          >
            {years.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" onClick={loadReport}>
          {t("detailingAm.get")}
        </Button>
      </Stack>

      <Box style={{ display: "flex", flexDirection: "column" }}>
        {!isAdmin && acts.length > 0 && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setSelectedActsAgent(null);
                  setShowActsPopup(true);
                }}
              >
                {t("detailingAm.openActs")}
              </Button>
            }
          >
            {t("detailingAm.actExists")}
          </Alert>
        )}

        {!isAdmin && (
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            sx={{
              p: 1,
              borderLeft: "3px solid " + brandColor,
              backgroundColor: "#FFFDE7",
              mb: 1,
            }}
          >
            <Typography variant="body2">
              <b>{t("detailingAm.summary.totalLabel")}</b>,{" "}
              {t("detailingAm.currencyIn", { currency })}
            </Typography>

            <Typography variant="body2">
              {t("detailingAm.summary.brokerage")}:{" "}
              <strong>{formatMoney(summary.brokerage)}</strong>
            </Typography>

            <Typography variant="body2">
              {t("detailingAm.summary.retention")}:{" "}
              <strong>{formatMoney(summary.retention)}</strong>
            </Typography>

            <Typography variant="body2">
              {t("detailingAm.summary.acquisition")}:{" "}
              <strong>{formatMoney(summary.acquisition)}</strong>
            </Typography>

            <Typography variant="body2">
              {t("detailingAm.summary.total")}:{" "}
              <strong>{formatMoney(summary.total)}</strong>
            </Typography>
          </Stack>
        )}

        <DataGrid
          rows={rows}
          columns={isAdmin ? mainColumns : detailColumns}
          loading={isLoading}
          getRowId={(row) => row.id}
          pagination
          onRowClick={(params) => {
            if (isAdmin) {
              setSelectedAgent(params.row);
              setShowDetailsPopup(true);
            }
          }}
          sx={{
            ...gridSx,
            ...(isAdmin
              ? {
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                }
              : {}),
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Box>

      <StyledDrawer
        show={showDetailsPopup}
        title={t("detailingAm.drawer.agentDetailing", {
          name: selectedAgent?.fullname ?? selectedAgent?.agent_tn_id ?? "",
        })}
        onClose={handleCloseDetailsPopup}
        ContentComponent={DetailsContent}
        contentProps={{}}
        width="80vw"
      />

      <StyledDrawer
        show={showActsPopup}
        title={
          isAdmin
            ? t("detailingAm.drawer.agentActs", {
                name:
                  selectedActsAgent?.fullname ??
                  selectedActsAgent?.agent_tn_id ??
                  "",
              })
            : t("detailingAm.drawer.myActs")
        }
        onClose={() => {
          setShowActsPopup(false);
          setSelectedActsAgent(null);
        }}
        ContentComponent={() => (
          <ArmeniaActsHistoryDrawer
            acts={getSelectedAgentActs()}
            isAdmin={isAdmin}
            onOpenAct={(act, lang) => {
              setActLang(lang);
              setActType(ActPrintType.ACT);
              setSelectedAct(act);
            }}
            onOpenDispute={(act) => {
              setSelectedDisputeAct(act);
              setShowDisputePopup(true);
            }}
            onOpenDisputePreview={(act) => {
              setSelectedDisputeAct(act);
              setShowDisputePreviewPopup(true);
            }}
            onOpenSignedAct={(act) => {
              setSelectedSignedFilesAct(act);
              setShowSignedFilesPopup(true);
            }}
            onUploadSignedAct={uploadSignedAct}
            onAgreeAct={agreeAct}
            onOpenSignedFiles={(act) => {
              setSelectedSignedFilesAct(act);
              setShowSignedFilesPopup(true);
            }}
          />
        )}
        contentProps={{}}
        width="75vw"
      />

      <StyledDrawer
        show={showDisputePopup}
        title={t("detailingAm.drawer.dispute")}
        onClose={() => {
          setShowDisputePopup(false);
          setSelectedDisputeAct(null);
        }}
        ContentComponent={() => (
          <ArmeniaActDisputeDrawer
            clients={getActClients(selectedDisputeAct)}
            loading={isDisputeSubmitting}
            onSubmit={submitDispute}
            onClose={() => {
              setShowDisputePopup(false);
              setSelectedDisputeAct(null);
            }}
          />
        )}
        contentProps={{}}
        width="60vw"
      />

      <StyledDrawer
        show={showDisputePreviewPopup}
        title={t("detailingAm.drawer.dispute")}
        onClose={() => {
          setShowDisputePreviewPopup(false);
          setSelectedDisputeAct(null);
        }}
        ContentComponent={() => {
          const disputes = (selectedDisputeAct as any)?.disputes ?? [];
          const dispute = disputes[0];

          return (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("detailingAm.dispute.agentComment")}:{" "}
                <strong>
                  {dispute?.comment ?? t("detailingAm.dispute.noComment")}
                </strong>
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("detailingAm.dispute.disputedAccounts")}:
              </Typography>

              <Stack spacing={1}>
                {(dispute?.items ?? []).map((item: any) => (
                  <Typography key={item.id} variant="body2">
                    {item.client_account}
                    {" — "}
                    {t("detailingAm.summary.brokerage").toLowerCase()}:{" "}
                    {Number(item.brokerage_base ?? 0).toFixed(2)},{" "}
                    {t("detailingAm.summary.retention").toLowerCase()}:{" "}
                    {Number(item.retention_base ?? 0).toFixed(2)},{" "}
                    {t("detailingAm.summary.acquisition").toLowerCase()}:{" "}
                    {Number(item.attraction_base ?? 0).toFixed(2)}
                  </Typography>
                ))}
              </Stack>
            </Box>
          );
        }}
        contentProps={{}}
        width="50vw"
      />

      <StyledDrawer
        show={showSignedFilesPopup}
        title={t("detailingAm.drawer.signedFiles")}
        onClose={() => {
          setShowSignedFilesPopup(false);
          setSelectedSignedFilesAct(null);
        }}
        ContentComponent={() => (
          <ArmeniaActSignedFilesDrawer
            act={selectedSignedFilesAct}
            onPreview={previewSignedFile}
          />
        )}
        contentProps={{}}
        width="70vw"
      />

      <PdfViewer
        open={
          Boolean(previewPdfBlob) || Boolean(previewError && !previewImageBlob)
        }
        onClose={() => {
          setPreviewPdfBlob(undefined);
          setPreviewFileName("");
          setPreviewError(undefined);
        }}
        title={previewFileName || t("detailingAm.preview.file")}
        pdfBlob={previewPdfBlob}
        loading={previewLoading}
        error={previewError}
      />

      <ImageViewer
        open={Boolean(previewImageBlob)}
        onClose={() => {
          setPreviewImageBlob(undefined);
          setPreviewFileName("");
          setPreviewError(undefined);
        }}
        title={previewFileName || t("detailingAm.preview.image")}
        imageBlob={previewImageBlob}
        loading={previewLoading}
        error={previewError}
      />

      {selectedAct && selectedAct.id > 0 && (
        <ActViewer
          act={selectedAct as any}
          type={actType}
          lang={actLang}
          onClose={() => setSelectedAct(undefined)}
          onAction={(action: ActAction) => {
            loadReport();
          }}
        />
      )}
    </>
  );
};

export default DetailingAm;
